/**
 * Administración: usuarios y roles, historial de descargas y centro de notificaciones.
 *
 * Requiere conexión: son operaciones del servidor y su autorización se valida allí,
 * no ocultando botones aquí.
 */

import { useCallback, useEffect, useState } from 'react';
import type { DownloadJobDto, NotificationDto, UserDto } from '@senm/contracts';
import { createIdGenerator, formatInstant, ROLE_LABELS, type Role } from '@senm/domain';
import { ApiRequestError, apiDownload, apiRequest } from '../api/client.js';
import { openPdf } from '../services/payments.js';
import { InstitutionConfigPage } from './InstitutionConfig.js';
import { sessionCan, type StoredSession } from '../local/session.js';

const newId = createIdGenerator();

export function AdminPage(props: { session: StoredSession; conexion: string }): JSX.Element {
  const [pestana, setPestana] = useState<'usuarios' | 'descargas' | 'notificaciones'>('usuarios');
  const [error, setError] = useState<string | null>(null);

  return (
    <>
      <h1>Administración</h1>

      <InstitutionConfigPage session={props.session} conexion={props.conexion} />

      <div className="pila">
        {(
          [
            ['usuarios', 'Usuarios y roles'],
            ['descargas', 'Historial de descargas'],
            ['notificaciones', 'Notificaciones'],
          ] as const
        ).map(([id, etiqueta]) => (
          <button
            key={id}
            type="button"
            className="boton"
            aria-pressed={pestana === id}
            onClick={() => {
              setPestana(id);
              setError(null);
            }}
          >
            {etiqueta}
          </button>
        ))}
      </div>

      {props.conexion === 'SIN_CONEXION' && (
        <div className="aviso-caja" role="status">
          Sin conexión: la administración requiere contacto con el servidor.
        </div>
      )}
      {error !== null && (
        <div className="aviso-caja error" role="alert">
          {error}
        </div>
      )}

      {pestana === 'usuarios' && <Usuarios session={props.session} onError={setError} />}
      {pestana === 'descargas' && <Descargas session={props.session} onError={setError} />}
      {pestana === 'notificaciones' && <Notificaciones session={props.session} onError={setError} />}
    </>
  );
}

// ---------------------------------------------------------------------------

function Usuarios(props: { session: StoredSession; onError: (mensaje: string | null) => void }): JSX.Element {
  const [usuarios, setUsuarios] = useState<UserDto[]>([]);
  const [nuevo, setNuevo] = useState({ username: '', fullName: '', password: '', rol: 'CAJA' as Role });

  const cargar = useCallback(async () => {
    try {
      const pagina = await apiRequest<{ items: UserDto[] }>('/v1/users');
      setUsuarios(pagina.items);
    } catch (problema) {
      props.onError(problema instanceof ApiRequestError ? problema.message : 'No fue posible cargar los usuarios.');
    }
  }, [props]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const crear = async (evento: React.FormEvent): Promise<void> => {
    evento.preventDefault();
    try {
      await apiRequest('/v1/users', {
        body: {
          id: newId(),
          username: nuevo.username.trim().toLowerCase(),
          fullName: nuevo.fullName.trim(),
          email: null,
          password: nuevo.password,
          roles: [nuevo.rol],
        },
      });
      setNuevo({ username: '', fullName: '', password: '', rol: 'CAJA' });
      props.onError(null);
      await cargar();
    } catch (problema) {
      props.onError(problema instanceof ApiRequestError ? problema.message : 'No fue posible crear el usuario.');
    }
  };

  /** Cambiar el rol de un usuario EXISTENTE, no solo al crearlo. */
  const cambiarRol = async (usuario: UserDto, rol: Role): Promise<void> => {
    try {
      await apiRequest(`/v1/users/${usuario.id}`, {
        method: 'PATCH',
        body: { roles: [rol], expectedVersion: usuario.version },
      });
      await cargar();
    } catch (problema) {
      props.onError(
        problema instanceof ApiRequestError && problema.httpStatus === 409
          ? 'El usuario cambió mientras lo editaba. Se recargó la lista; intente de nuevo.'
          : 'No fue posible cambiar el rol.',
      );
      await cargar();
    }
  };

  const puedeAdministrar = sessionCan(props.session, 'usuarios:administrar');

  return (
    <>
      <h2>Usuarios</h2>
      <div className="tarjeta tabla-desplazable">
        <table>
          <thead>
            <tr>
              <th scope="col">Usuario</th>
              <th scope="col">Nombre</th>
              <th scope="col">Rol</th>
              <th scope="col">Estado</th>
              <th scope="col">Último acceso</th>
            </tr>
          </thead>
          <tbody>
            {usuarios.map((usuario) => (
              <tr key={usuario.id}>
                <td>{usuario.username}</td>
                <td>{usuario.fullName}</td>
                <td>
                  {puedeAdministrar ? (
                    <select
                      value={usuario.roles[0] ?? 'CAJA'}
                      aria-label={`Rol de ${usuario.username}`}
                      onChange={(evento) => void cambiarRol(usuario, evento.target.value as Role)}
                    >
                      {(Object.keys(ROLE_LABELS) as Role[]).map((rol) => (
                        <option key={rol} value={rol}>
                          {ROLE_LABELS[rol]}
                        </option>
                      ))}
                    </select>
                  ) : (
                    usuario.roles.map((rol) => ROLE_LABELS[rol]).join(', ')
                  )}
                </td>
                <td>
                  <span className={`marca-estado ${usuario.active ? 'ok' : 'error'}`}>
                    {usuario.active ? '✓ Activo' : '✕ Inactivo'}
                  </span>
                </td>
                <td>
                  {usuario.lastLoginAt === null
                    ? 'Nunca'
                    : formatInstant(usuario.lastLoginAt, props.session.user.timeZone)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {puedeAdministrar && (
        <form className="tarjeta" onSubmit={(evento) => void crear(evento)}>
          <h2>Crear usuario</h2>
          <div className="rejilla">
            <label>
              <span>Usuario</span>
              <input
                value={nuevo.username}
                onChange={(evento) => setNuevo({ ...nuevo, username: evento.target.value })}
                required
                minLength={3}
              />
            </label>
            <label>
              <span>Nombre completo</span>
              <input
                value={nuevo.fullName}
                onChange={(evento) => setNuevo({ ...nuevo, fullName: evento.target.value })}
                required
                minLength={3}
              />
            </label>
            <label>
              <span>Contraseña (mínimo 10 caracteres)</span>
              <input
                type="password"
                value={nuevo.password}
                onChange={(evento) => setNuevo({ ...nuevo, password: evento.target.value })}
                required
                minLength={10}
                autoComplete="new-password"
              />
            </label>
            <label>
              <span>Rol</span>
              <select value={nuevo.rol} onChange={(evento) => setNuevo({ ...nuevo, rol: evento.target.value as Role })}>
                {(Object.keys(ROLE_LABELS) as Role[]).map((rol) => (
                  <option key={rol} value={rol}>
                    {ROLE_LABELS[rol]}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <button type="submit" className="boton primario">
            Crear usuario
          </button>
        </form>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

function Descargas(props: { session: StoredSession; onError: (mensaje: string | null) => void }): JSX.Element {
  const [trabajos, setTrabajos] = useState<DownloadJobDto[]>([]);

  const cargar = useCallback(async () => {
    try {
      const pagina = await apiRequest<{ items: DownloadJobDto[] }>('/v1/downloads');
      setTrabajos(pagina.items);
    } catch {
      props.onError('No fue posible cargar el historial de descargas.');
    }
  }, [props]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const descargar = async (trabajo: DownloadJobDto): Promise<void> => {
    try {
      const archivo = await apiDownload(`/v1/downloads/${trabajo.id}/file`);
      openPdf(archivo.bytes, archivo.fileName);
      await cargar();
    } catch (problema) {
      // Si el temporal ya no existe, el servidor responde 410 y se regenera desde el historial.
      if (problema instanceof ApiRequestError && problema.httpStatus === 410) {
        props.onError('El archivo ya no estaba disponible. Use «Regenerar» para volver a crearlo.');
      } else {
        props.onError('No fue posible descargar el archivo.');
      }
      await cargar();
    }
  };

  /** La regeneración desde el historial es silenciosa: no genera notificación. */
  const regenerar = async (trabajo: DownloadJobDto): Promise<void> => {
    try {
      await apiRequest(`/v1/downloads/${trabajo.id}/regenerate`, { method: 'POST' });
      await cargar();
    } catch {
      props.onError('No fue posible regenerar el archivo.');
    }
  };

  return (
    <>
      <h2>Historial de descargas</h2>
      {trabajos.length === 0 ? (
        <p className="vacio">Sin descargas registradas.</p>
      ) : (
        <div className="tarjeta tabla-desplazable">
          <table>
            <thead>
              <tr>
                <th scope="col">Fecha</th>
                <th scope="col" className="numero">Nº</th>
                <th scope="col">Estudiante</th>
                <th scope="col">Grado</th>
                <th scope="col">Archivo</th>
                <th scope="col">Estado</th>
                <th scope="col">
                  <span className="saltar">Acciones</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {trabajos.map((trabajo) => (
                <tr key={trabajo.id}>
                  <td>{trabajo.operationalDate}</td>
                  <td className="numero">{trabajo.number}</td>
                  <td>{trabajo.studentName}</td>
                  <td>{trabajo.gradeName}</td>
                  <td>{trabajo.fileName}</td>
                  <td>
                    <span className={`marca-estado ${trabajo.status === 'FALLIDO' ? 'error' : ''}`}>
                      {trabajo.status}
                    </span>
                    {trabajo.errorMessage !== null && <p className="ayuda">{trabajo.errorMessage}</p>}
                  </td>
                  <td>
                    <div className="pila">
                      {trabajo.available && (
                        <button type="button" className="boton" onClick={() => void descargar(trabajo)}>
                          Descargar
                        </button>
                      )}
                      {!trabajo.available && (
                        <button type="button" className="boton" onClick={() => void regenerar(trabajo)}>
                          Regenerar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

function Notificaciones(props: { session: StoredSession; onError: (mensaje: string | null) => void }): JSX.Element {
  const [notificaciones, setNotificaciones] = useState<NotificationDto[]>([]);

  const cargar = useCallback(async () => {
    try {
      setNotificaciones(await apiRequest<NotificationDto[]>('/v1/notifications'));
    } catch {
      props.onError('No fue posible cargar las notificaciones.');
    }
  }, [props]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const cerrar = async (notificacion: NotificationDto): Promise<void> => {
    await apiRequest(`/v1/notifications/${notificacion.id}`, { method: 'DELETE' });
    await cargar();
  };

  /** Única acción para un archivo pendiente, con el texto exacto de confirmación. */
  const resolver = async (notificacion: NotificationDto): Promise<void> => {
    const confirmar = window.confirm('¿Desea marcar el caso como resuelto?');
    if (!confirmar) {
      // Cancelar cierra la notificación pero conserva el caso pendiente: volverá a
      // notificarse al iniciar una nueva sesión de Administración.
      await cerrar(notificacion);
      return;
    }
    try {
      await apiRequest(`/v1/notifications/${notificacion.id}/resolve`, { method: 'POST' });
      await cargar();
    } catch {
      props.onError('No fue posible marcar el caso como resuelto.');
    }
  };

  return (
    <>
      <h2>Centro de notificaciones</h2>
      {notificaciones.length === 0 ? (
        <p className="vacio">No hay notificaciones.</p>
      ) : (
        notificaciones.map((notificacion) => (
          <section className="tarjeta" key={notificacion.id}>
            <strong>{notificacion.title}</strong>
            <p>{notificacion.body}</p>
            <p className="ayuda">{formatInstant(notificacion.createdAt, props.session.user.timeZone)}</p>
            <div className="pila">
              {notificacion.actionLabel !== null && (
                <button type="button" className="boton primario" onClick={() => void resolver(notificacion)}>
                  {notificacion.actionLabel}
                </button>
              )}
              <button type="button" className="boton" onClick={() => void cerrar(notificacion)}>
                Cerrar
              </button>
            </div>
          </section>
        ))
      )}
    </>
  );
}
